"use client"

import React, { useState, useEffect } from "react"
import { MapPin, ChevronDown, Search } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// Philippine Geo Hierarchy Data
export const philippineLocations = {
  regions: [
    { id: "ncr", name: "Metro Manila (NCR)" },
    { id: "calabarzon", name: "CALABARZON (Region IV-A)" },
    { id: "central-luzon", name: "Central Luzon (Region III)" },
    { id: "central-visayas", name: "Central Visayas (Region VII)" },
    { id: "davao", name: "Davao Region (Region XI)" },
  ],
  cities: {
    ncr: [
      { id: "makati", name: "Makati City" },
      { id: "taguig", name: "Taguig City" },
      { id: "quezon-city", name: "Quezon City" },
      { id: "pasig", name: "Pasig City" },
      { id: "manila", name: "Manila" },
      { id: "mandaluyong", name: "Mandaluyong City" },
      { id: "pasay", name: "Pasay City" },
      { id: "paranaque", name: "Parañaque City" },
      { id: "muntinlupa", name: "Muntinlupa City" },
      { id: "las-pinas", name: "Las Piñas City" },
    ],
    calabarzon: [
      { id: "cavite", name: "Cavite City" },
      { id: "laguna", name: "Laguna" },
      { id: "batangas", name: "Batangas City" },
      { id: "rizal", name: "Rizal" },
      { id: "quezon", name: "Quezon Province" },
    ],
    "central-luzon": [
      { id: "pampanga", name: "Pampanga" },
      { id: "bulacan", name: "Bulacan" },
      { id: "nueva-ecija", name: "Nueva Ecija" },
    ],
    "central-visayas": [
      { id: "cebu", name: "Cebu City" },
      { id: "bohol", name: "Bohol" },
    ],
    davao: [
      { id: "davao-city", name: "Davao City" },
    ],
  },
  neighborhoods: {
    makati: [
      "Salcedo Village",
      "Legazpi Village",
      "Ayala",
      "Bel-Air",
      "Urdaneta",
      "San Lorenzo",
      "Rockwell",
    ],
    taguig: [
      "BGC (Bonifacio Global City)",
      "Fort Bonifacio",
      "McKinley Hill",
      "Ususan",
      "Western Bicutan",
    ],
    "quezon-city": [
      "Diliman",
      "Cubao",
      "Eastwood",
      "Katipunan",
      "Commonwealth",
      "Novaliches",
    ],
    pasig: [
      "Ortigas Center",
      "Kapitolyo",
      "Manggahan",
      "Santolan",
    ],
    manila: [
      "Ermita",
      "Malate",
      "Binondo",
      "Quiapo",
      "Intramuros",
    ],
  },
}

interface LocationDropdownProps {
  onLocationChange?: (location: { region?: string; city?: string; neighborhood?: string }) => void
  className?: string
}

export function LocationDropdown({ onLocationChange, className }: LocationDropdownProps) {
  const [selectedRegion, setSelectedRegion] = useState<string>("")
  const [selectedCity, setSelectedCity] = useState<string>("")
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>("")
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)

  const availableCities = selectedRegion ? philippineLocations.cities[selectedRegion as keyof typeof philippineLocations.cities] || [] : []
  const availableNeighborhoods = selectedCity ? philippineLocations.neighborhoods[selectedCity as keyof typeof philippineLocations.neighborhoods] || [] : []

  useEffect(() => {
    if (onLocationChange) {
      onLocationChange({
        region: selectedRegion || undefined,
        city: selectedCity || undefined,
        neighborhood: selectedNeighborhood || undefined,
      })
    }
  }, [selectedRegion, selectedCity, selectedNeighborhood, onLocationChange])

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value)
    setSelectedCity("")
    setSelectedNeighborhood("")
  }

  const handleCityChange = (value: string) => {
    setSelectedCity(value)
    setSelectedNeighborhood("")
  }

  const clearSelection = () => {
    setSelectedRegion("")
    setSelectedCity("")
    setSelectedNeighborhood("")
    setSearchQuery("")
  }

  const getDisplayText = () => {
    if (selectedNeighborhood) return selectedNeighborhood
    if (selectedCity) {
      const city = availableCities.find(c => c.id === selectedCity)
      return city?.name || selectedCity
    }
    if (selectedRegion) {
      const region = philippineLocations.regions.find(r => r.id === selectedRegion)
      return region?.name || selectedRegion
    }
    return "All Locations"
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-between font-normal ${className}`}
          aria-label="Select location"
        >
          <div className="flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="flex flex-col gap-2 p-4">
          {/* Quick Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Quick search location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Region Selector */}
          <Select value={selectedRegion} onValueChange={handleRegionChange}>
            <SelectTrigger aria-label="Select region">
              <SelectValue placeholder="Select Region" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Regions</SelectLabel>
                {philippineLocations.regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {/* City Selector */}
          {selectedRegion && availableCities.length > 0 && (
            <Select value={selectedCity} onValueChange={handleCityChange}>
              <SelectTrigger aria-label="Select city">
                <SelectValue placeholder="Select City" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Cities</SelectLabel>
                  {availableCities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}

          {/* Neighborhood Selector */}
          {selectedCity && availableNeighborhoods.length > 0 && (
            <Select value={selectedNeighborhood} onValueChange={setSelectedNeighborhood}>
              <SelectTrigger>
                <SelectValue placeholder="Select Neighborhood/Village" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Neighborhoods & Villages</SelectLabel>
                  {availableNeighborhoods.map((neighborhood) => (
                    <SelectItem key={neighborhood} value={neighborhood}>
                      {neighborhood}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              className="flex-1"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setIsOpen(false)}
              className="flex-1"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Compact inline version for hero search
export function LocationDropdownInline({ onLocationChange, className }: LocationDropdownProps) {
  const [selectedCity, setSelectedCity] = useState<string>("")

  const allCities = Object.values(philippineLocations.cities).flat()

  const handleCityChange = (value: string) => {
    setSelectedCity(value)
    const city = allCities.find(c => c.id === value)
    if (onLocationChange && city) {
      onLocationChange({ city: city.name })
    }
  }

  return (
    <Select value={selectedCity} onValueChange={handleCityChange}>
      <SelectTrigger className={`border-r-0 rounded-r-none ${className}`}>
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <SelectValue placeholder="All Locations" />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Metro Manila</SelectLabel>
          {philippineLocations.cities.ncr.map((city) => (
            <SelectItem key={city.id} value={city.id}>
              {city.name}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>CALABARZON</SelectLabel>
          {philippineLocations.cities.calabarzon.map((city) => (
            <SelectItem key={city.id} value={city.id}>
              {city.name}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Central Visayas</SelectLabel>
          {philippineLocations.cities["central-visayas"].map((city) => (
            <SelectItem key={city.id} value={city.id}>
              {city.name}
            </SelectItem>
          ))}
        </SelectGroup>
        <SelectGroup>
          <SelectLabel>Other Regions</SelectLabel>
          {philippineLocations.cities["central-luzon"].map((city) => (
            <SelectItem key={city.id} value={city.id}>
              {city.name}
            </SelectItem>
          ))}
          {philippineLocations.cities.davao.map((city) => (
            <SelectItem key={city.id} value={city.id}>
              {city.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
